/**
 * Report-table column chooser (WSRADM-0051).
 *
 * A `Columns` button opens a Menu of checkbox items (checked = visible) plus
 * Show all. The menu stays open while toggling; Esc closes it and returns
 * focus to the button. Hiding is display only — never a recomputed total.
 */
import { useId, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useCopy } from '@/hooks/useCopy';

export interface ColumnChooserOption {
  key: string;
  label: string;
  group?: string;
  /** Optional leading adornment (e.g. a family colour swatch). */
  adornment?: ReactNode;
}

export interface ColumnChooserProps {
  options: readonly ColumnChooserOption[];
  hidden: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onShowAll: () => void;
  buttonLabel?: string;
}

export function ColumnChooser({
  options,
  hidden,
  onToggle,
  onShowAll,
  buttonLabel,
}: ColumnChooserProps) {
  const { t } = useCopy();
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const hiddenCount = options.filter((option) => hidden.has(option.key)).length;
  const visibleCount = options.length - hiddenCount;
  const lastColumn = t('views.lastColumn', 'At least one column stays visible.');

  const items: ReactNode[] = [];
  let group: string | undefined;
  options.forEach((option) => {
    if (option.group !== undefined && option.group !== group) {
      items.push(<ListSubheader key={`group-${option.group}`}>{option.group}</ListSubheader>);
    } else if (option.group === undefined && group !== undefined) {
      items.push(<Divider key={`end-${group}`} />);
    }
    group = option.group;
    const visible = !hidden.has(option.key);
    const isLast = visible && visibleCount <= 1;
    const labelId = `${menuId}-${option.key}`;
    items.push(
      <MenuItem
        key={option.key}
        role="menuitemcheckbox"
        aria-checked={visible}
        aria-disabled={isLast ? true : undefined}
        title={isLast ? lastColumn : undefined}
        onClick={isLast ? undefined : () => onToggle(option.key)}
        dense
      >
        <ListItemIcon>
          <Checkbox
            edge="start"
            size="small"
            checked={visible}
            tabIndex={-1}
            disableRipple
            slotProps={{ input: { 'aria-labelledby': labelId } }}
          />
        </ListItemIcon>
        {option.adornment ?? null}
        <ListItemText
          id={labelId}
          primary={option.label}
          secondary={isLast ? lastColumn : undefined}
        />
      </MenuItem>,
    );
  });

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ViewColumnIcon />}
        aria-haspopup="menu"
        aria-controls={anchor !== null ? menuId : undefined}
        aria-expanded={anchor !== null ? true : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        {buttonLabel ?? t('views.columns', 'Columns')}
        {hiddenCount > 0 ? (
          <Chip
            size="small"
            label={t('views.hiddenBadge', '{{count}} hidden', { count: hiddenCount })}
            sx={{ ml: 1, height: 20 }}
            data-testid="column-chooser-badge"
          />
        ) : null}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: '60vh' } } }}
      >
        {items}
        <Divider />
        <MenuItem disabled={hiddenCount === 0} onClick={onShowAll} dense>
          {t('views.showAll', 'Show all')}
        </MenuItem>
      </Menu>
    </>
  );
}

export interface HiddenHintProps {
  count: number;
  kind: 'columns' | 'series';
  onShowAll: () => void;
}

/** Muted "N columns hidden · Show all" line under a table or chart. */
export function HiddenHint({ count, kind, onShowAll }: HiddenHintProps) {
  const { t } = useCopy();
  if (count <= 0) return null;
  const text =
    kind === 'series'
      ? t('views.hiddenSeriesHint', '{{count}} series hidden', { count })
      : t('views.hiddenHint', '{{count}} columns hidden', { count });
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}
      data-testid="hidden-hint"
    >
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
      <Button size="small" variant="text" onClick={onShowAll}>
        {t('views.showAll', 'Show all')}
      </Button>
    </Box>
  );
}
